import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Save, Upload, Monitor, Tablet, Smartphone, Plus, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, Settings2, RotateCcw, ExternalLink, Sparkles, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { SectionInspector } from './SectionInspector';
import { SeoPanel } from './SeoPanel';
import { AddSectionDialog } from './AddSectionDialog';
import { AiAssistant } from './AiAssistant';
import { uid } from './api';

const VIEWPORTS = { desktop: '100%', tablet: '820px', mobile: '390px' };

const PreviewFrame = ({ sections, selectedId, viewport, onSelect }) => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.source !== ref.current?.contentWindow) return;
      if (e.data?.type === 'cms-preview-ready') setReady(true);
      if (e.data?.type === 'cms-select') onSelect(e.data.id);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [onSelect]);
  useEffect(() => {
    if (ready) ref.current?.contentWindow?.postMessage({ type: 'cms-preview', sections, selectedId }, '*');
  }, [ready, sections, selectedId]);
  return (
    <iframe ref={ref} title="Page preview" src="/admin/preview-frame" data-testid="builder-preview-iframe" data-viewport={viewport}
      className="bg-white shadow-xl transition-all duration-300 h-full" style={{ width: VIEWPORTS[viewport], maxWidth: '100%' }} />
  );
};

// Visual page builder: section list (left) · live preview (center) · inspector / SEO (right)
// initialFocus: { panel: 'seo', ai: true } opens the SEO panel and pre-generates AI suggestions (review-only, nothing saved);
//               { sectionId } selects a section (used by the broken-link report).
export const PageBuilder = ({ api, slug, registry, pages, canPublish, onBack, onSaved, initialFocus }) => {
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewport, setViewport] = useState('desktop');
  const [resolved, setResolved] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [rightPanel, setRightPanel] = useState('section'); // section | seo | ai
  const [aiTarget, setAiTarget] = useState(null);
  const [seoSuggestions, setSeoSuggestions] = useState({});
  const [aiBusy, setAiBusy] = useState(null);
  const dragIndex = useRef(null);
  const specs = useMemo(() => Object.fromEntries((registry?.sections || []).map((s) => [s.type, s])), [registry]);

  useEffect(() => {
    api.page(slug).then((p) => { setPage(p); setDraft(p.draft); setDirty(false); setFocusPending(!!initialFocus); }).catch(() => toast.error('Could not load page'));
  }, [slug, api]); // eslint-disable-line react-hooks/exhaustive-deps

  // apply the requested focus once the draft is loaded
  const [focusPending, setFocusPending] = useState(false);
  useEffect(() => {
    if (!focusPending || !draft) return;
    setFocusPending(false);
    if (initialFocus?.sectionId) {
      setSelectedId(initialFocus.sectionId); setRightPanel('section');
      if (initialFocus.imageUrl) setTimeout(() => window.dispatchEvent(new CustomEvent('builder-focus-image', { detail: { imageUrl: initialFocus.imageUrl } })), 400);
      return;
    }
    if (initialFocus?.panel === 'seo') {
      setSelectedId(null); setRightPanel('seo');
      setTimeout(() => document.getElementById('builder-seo-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      if (initialFocus.ai) runSeoAi('all');
    }
  }, [focusPending, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  // resolve data sources for live preview (debounced per section source signature)
  useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => {
      (draft.sections || []).forEach((s) => {
        if (!s.source) return;
        const key = JSON.stringify(s.source);
        if (resolved[key] !== undefined) return;
        api.resolveSource(s.source).then((data) => setResolved((r) => ({ ...r, [key]: data }))).catch(() => {});
      });
    }, 300);
    return () => clearTimeout(t);
  }, [draft, api, resolved]);

  const previewSections = useMemo(() => (draft?.sections || []).map((s) => (s.source ? { ...s, data: resolved[JSON.stringify(s.source)] || [] } : s)), [draft, resolved]);
  const update = useCallback((patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); }, []);
  const setSections = (fn) => update({ sections: fn(draft.sections || []) });
  const selected = draft?.sections?.find((s) => s.id === selectedId);

  const addSection = async (type) => {
    const sec = await api.newSection({ type });
    setSections((ss) => [...ss, sec]);
    setSelectedId(sec.id);
    setRightPanel('section');
  };
  const changeSection = (next) => setSections((ss) => ss.map((s) => (s.id === next.id ? next : s)));
  const duplicateSection = (id) => setSections((ss) => { const i = ss.findIndex((s) => s.id === id); const copy = { ...JSON.parse(JSON.stringify(ss[i])), id: uid(ss[i].type) }; return [...ss.slice(0, i + 1), copy, ...ss.slice(i + 1)]; });
  const deleteSection = (id) => { setSections((ss) => ss.filter((s) => s.id !== id)); if (selectedId === id) setSelectedId(null); };
  const move = (from, to) => { if (to < 0 || to >= draft.sections.length) return; setSections((ss) => { const n = [...ss]; const [it] = n.splice(from, 1); n.splice(to, 0, it); return n; }); };

  const save = async () => {
    setSaving(true);
    try {
      await api.saveDraft(slug, { sections: draft.sections, seo: draft.seo, name: draft.name, path: draft.path, nav_label: draft.nav_label, show_in_nav: draft.show_in_nav, template: draft.template, featured_image: draft.featured_image });
      setDirty(false);
      toast.success('Draft saved');
      onSaved?.();
      return true;
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); return false; } finally { setSaving(false); }
  };
  const publish = async () => {
    if (dirty && !(await save())) return;
    try { const r = await api.publish(slug); toast.success(`Published at ${r.path}`); const p = await api.page(slug); setPage(p); setDraft(p.draft); onSaved?.(); } catch (err) { toast.error(err.response?.data?.detail || 'Publish failed'); }
  };
  const discard = async () => {
    if (!window.confirm('Discard all unpublished changes and revert to the published version?')) return;
    const r = await api.discardDraft(slug); setDraft(r.draft); setDirty(false); toast.success('Draft discarded');
  };
  const openAi = (section, field) => { setAiTarget({ sectionId: section?.id, field }); setRightPanel('ai'); };
  const setSchedule = async (patch) => {
    try { await api.saveDraft(slug, patch); setPage((p) => ({ ...p, ...patch })); toast.success(Object.values(patch)[0] ? 'Schedule saved' : 'Schedule cleared'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const importLegacy = async () => {
    if (!window.confirm('Replace the current draft with a builder version of the live coded page? (Nothing goes live until you publish and switch rendering.)')) return;
    try { const r = await api.importLegacy(slug); setDraft(r.draft); setDirty(false); setSelectedId(null); setResolved({}); toast.success('Layout imported — review, then publish'); setPage((p) => ({ ...p, has_unpublished_changes: true })); } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
  };
  const toggleBuilder = async (on) => {
    if (on && !page.sections?.length) return toast.error('Publish the builder version first, then switch rendering');
    const msg = on
      ? 'Migration rule: the coded page is the visual source of truth. Only continue if you have compared the builder version with the original on desktop, tablet AND mobile and approved it.\n\nVisitors will now see the builder version at this URL. Continue?'
      : 'Switch back to the original coded page?';
    if (!window.confirm(msg)) return;
    try { await api.saveDraft(slug, { builder_enabled: on }); setPage((p) => ({ ...p, builder_enabled: on })); toast.success(on ? 'Builder version is now live' : 'Coded page restored'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const runSeoAi = async (field) => {
    setAiBusy(field);
    try {
      const res = await api.ai('seo', { slug, sections: draft.sections, seo: draft.seo, name: draft.name, path: draft.path, fields: field === 'all' ? null : [field] });
      setSeoSuggestions((s) => ({ ...s, ...res.suggestions }));
      toast.success('Suggestions ready — review and apply');
    } catch (err) { toast.error(err.response?.data?.detail || 'AI request failed'); } finally { setAiBusy(null); }
  };
  const applySuggestion = (field) => { update({ seo: { ...draft.seo, [field]: seoSuggestions[field] } }); setSeoSuggestions((s) => { const n = { ...s }; delete n[field]; return n; }); };

  if (!page || !draft) return <div className="p-10 text-center text-neutral-500">Loading builder…</div>;
  const isBuiltin = !page.is_custom;

  return (
    <div className="fixed inset-0 z-50 bg-neutral-100 flex flex-col" data-testid="page-builder">
      <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-4 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => { if (!dirty || window.confirm('You have unsaved changes. Leave anyway?')) onBack(); }} className="p-2 hover:bg-neutral-100" data-testid="builder-back"><ArrowLeft className="w-4 h-4" /></button>
          <div className="min-w-0"><p className="font-semibold text-neutral-900 truncate">{draft.name}</p><p className="text-xs text-neutral-500 font-mono truncate">{draft.path}</p></div>
          <span className={`text-[11px] px-2 py-0.5 uppercase tracking-wide ${page.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-neutral-200 text-neutral-700'}`} data-testid="builder-status">{page.status}</span>
          {(dirty || page.has_unpublished_changes) && <span className="text-[11px] text-amber-700">Unpublished changes</span>}
        </div>
        <div className="flex items-center gap-1 bg-neutral-100 p-1">
          {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([v, I]) => <button key={v} onClick={() => setViewport(v)} className={`p-1.5 ${viewport === v ? 'bg-white shadow-sm' : 'text-neutral-500'}`} title={v} data-testid={`viewport-${v}`}><I className="w-4 h-4" /></button>)}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setRightPanel('ai')} className={`p-2 hover:bg-neutral-100 ${rightPanel === 'ai' ? 'text-amber-700' : ''}`} title="AI assistant" data-testid="builder-ai-btn"><Sparkles className="w-4 h-4" /></button>
          <button onClick={() => setRightPanel('seo')} className={`p-2 hover:bg-neutral-100 ${rightPanel === 'seo' ? 'text-amber-700' : ''}`} title="Page settings & SEO" data-testid="builder-seo-btn"><Settings2 className="w-4 h-4" /></button>
          {page.status === 'published' && <a href={page.path} target="_blank" rel="noreferrer" className="p-2 hover:bg-neutral-100" title="View live page" data-testid="builder-view-live"><ExternalLink className="w-4 h-4" /></a>}
          {page.has_unpublished_changes && page.status === 'published' && <button onClick={discard} className="p-2 hover:bg-neutral-100" title="Discard draft" data-testid="builder-discard"><RotateCcw className="w-4 h-4" /></button>}
          <Button variant="outline" size="sm" onClick={save} disabled={saving || !dirty} data-testid="builder-save"><Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save draft'}</Button>
          {canPublish && <Button size="sm" onClick={publish} className="bg-neutral-900" data-testid="builder-publish"><Upload className="w-4 h-4 mr-1.5" />Publish</Button>}
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <aside className="w-64 bg-white border-r border-neutral-200 flex flex-col">
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Sections</span><button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium" data-testid="add-section-btn"><Plus className="w-3.5 h-3.5" />Add</button></div>
          <div className="flex-1 overflow-y-auto">
            {(draft.sections || []).length === 0 && <p className="p-4 text-xs text-neutral-400">No sections yet. Click “Add” to start building.</p>}
            {(draft.sections || []).map((s, i) => (
              <div key={s.id} draggable onDragStart={() => { dragIndex.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragIndex.current !== null) move(dragIndex.current, i); dragIndex.current = null; }}
                onClick={() => { setSelectedId(s.id); setRightPanel('section'); }} data-testid={`section-item-${s.id}`}
                className={`group flex items-center gap-2 px-3 py-2.5 border-b border-neutral-100 cursor-pointer text-sm ${selectedId === s.id ? 'bg-amber-50' : 'hover:bg-neutral-50'} ${s.hidden ? 'opacity-50' : ''}`}>
                <GripVertical className="w-4 h-4 text-neutral-300 cursor-grab" />
                <div className="flex-1 min-w-0"><p className="truncate font-medium text-neutral-800">{specs[s.type]?.label || s.type}</p><p className="truncate text-[11px] text-neutral-400">{s.props?.title || specs[s.type]?.layouts.find((l) => l.id === s.layout)?.label}</p></div>
                <div className="hidden group-hover:flex items-center">
                  <button onClick={(e) => { e.stopPropagation(); move(i, i - 1); }} className="p-0.5 hover:bg-neutral-200"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); move(i, i + 1); }} className="p-0.5 hover:bg-neutral-200"><ChevronDown className="w-3.5 h-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); changeSection({ ...s, hidden: !s.hidden }); }} className="p-0.5 hover:bg-neutral-200">{s.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setAddOpen(true)} className="m-3 border border-dashed border-neutral-300 py-2.5 text-sm text-neutral-600 hover:border-neutral-500 inline-flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" />Add section</button>
        </aside>

        <main className="flex-1 overflow-hidden p-6 flex justify-center bg-neutral-200/60" data-testid="builder-preview">
          <PreviewFrame sections={previewSections} selectedId={selectedId} viewport={viewport} onSelect={(id) => { setSelectedId(id); setRightPanel('section'); }} />
        </main>

        <aside className="w-[380px] bg-white border-l border-neutral-200 flex flex-col min-h-0">
          {rightPanel === 'ai' ? (
            <AiAssistant api={api} draft={draft} slug={slug} target={aiTarget} specs={specs} onApplyText={(sectionId, field, text) => { const s = draft.sections.find((x) => x.id === sectionId); if (s) changeSection({ ...s, props: { ...s.props, [field]: text } }); }}
              onReplaceSections={(sections) => { update({ sections }); setSelectedId(null); }} onAppendSections={(sections) => setSections((ss) => [...ss, ...sections])} onClose={() => setRightPanel(selected ? 'section' : 'seo')} />
          ) : rightPanel === 'seo' || !selected ? (
            <SeoPanel api={api} draft={draft} page={page} onChange={update} isBuiltin={isBuiltin} canPublish={canPublish} onToggleBuilder={toggleBuilder} onImportLegacy={importLegacy} onSchedule={setSchedule} onAi={runSeoAi} aiBusy={aiBusy} suggestions={seoSuggestions} onApplySuggestion={applySuggestion} onDismissSuggestion={(f) => setSeoSuggestions((s) => { const n = { ...s }; delete n[f]; return n; })} />
          ) : (
            <SectionInspector api={api} section={selected} spec={specs[selected.type]} pages={pages} onChange={changeSection} onDuplicate={() => duplicateSection(selected.id)} onDelete={() => deleteSection(selected.id)} onAi={openAi} pageName={draft?.name} />
          )}
        </aside>
      </div>
      <AddSectionDialog open={addOpen} onClose={() => setAddOpen(false)} registry={registry?.sections} onAdd={addSection} />
    </div>
  );
};
